import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  DateRange,
  extractDateRange,
  extractDefaultStoreIds,
  getCurrencySymbol,
  HxReportService,
  HxStoreService,
  HxUiLabelPipe,
  isoDate,
  PaymentInfoModel,
  PaymentType,
  RevenueModel,
  toRouterQueryParams
} from 'hx-services';
import { ErrorHandlerService } from '@manager-app/service/error-handler.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { HxCitySelectComponent, HxCountrySelectComponent, HxDateRangeSelectComponent, HxLoaderComponent, HxStoreSelectComponent } from 'hx-component';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';

interface MoneyReportRow extends RevenueModel {
  paymentMap?: Map<PaymentType, PaymentInfoModel>;
}

/**
 * Component report list of users, how much sold orders and cash paid
 * Денежный отчёт
 */
@Component({
    selector: 'app-money-report.m-grid__item.m-grid__item--fluid.m-wrapper',
    templateUrl: './money-report.component.html',
    styleUrls: ['./money-report.component.css'],
    imports: [NgIf, HxCountrySelectComponent, FormsModule, HxCitySelectComponent, HxStoreSelectComponent, HxDateRangeSelectComponent, NgFor, NgClass, RouterLink, HxLoaderComponent, TranslocoPipe, HxUiLabelPipe]
})
export class MoneyReportComponent implements OnInit, OnDestroy {
  @ViewChild('templateRef') templateRef: TemplateRef<any>;

  list: MoneyReportRow[] = [];
  isLoading = {
    list: false,
  };
  params: {
    storeIds?: number[],
    date?: DateRange,
    countryId?: number,
    cityId?: number,
  } = {};
  paymentTypes: PaymentType[] = [];
  paymentType = PaymentType;
  currencySymbol: string;
  columns: PaymentType[] = [];
  tooManyStores: boolean = false;
  private $destroyed = new Subject<void>();

  constructor(
    private reportService: HxReportService,
    private aRoute: ActivatedRoute,
    private router: Router,
    private storeService: HxStoreService,
    private errorService: ErrorHandlerService,
  ) {
  }

  ngOnInit() {
    this.aRoute.queryParamMap.pipe(takeUntil(this.$destroyed)).subscribe(async paramMap => {
      const asArrFn = (name: string, mapFn: (key: string) => any) => paramMap.get(name)?.split(',').map(mapFn) ?? undefined;
      this.params.date = extractDateRange('date', paramMap) ?? {from: isoDate(new Date()), to: isoDate(new Date())};
      this.params.storeIds = asArrFn('storeIds', str => Number(str)) ?? extractDefaultStoreIds();
      this.loadMyStores();
      if (this.params.storeIds.length > 0) {
        this.storeService.getFullStore(this.params.storeIds[0]).then(store => {
          this.currencySymbol = getCurrencySymbol(store.currency);
          this.loadActivePaymentTypes(store.id);
        });
        await this.loadList();
      } else {
        this.currencySymbol = undefined;
        this.paymentTypes = [];
      }
    });
  }

  ngOnDestroy() {
    this.$destroyed.next();
    this.$destroyed.complete();
  }

  async loadList() {
    this.isLoading.list = true;
    this.list = [];

    const reportList: MoneyReportRow[] = await this.reportService.getRevenue(this.params);
    const columns: PaymentType[] = [];
    const paymentTypeOrder = Object.values(PaymentType);
    reportList.forEach(mr => {
      mr.paymentMap = new Map<PaymentType, PaymentInfoModel>();
      mr.payments.forEach(pi => {
        mr.paymentMap.set(pi.type, pi);
        if (!columns.includes(pi.type) && pi.value !== 0) {
          columns.push(pi.type);
        }
      });
    });
    this.columns = columns.sort((a, b) => paymentTypeOrder.indexOf(a) - paymentTypeOrder.indexOf(b));
    this.list = reportList;
    this.isLoading.list = false;

  }

  navigate() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  private loadActivePaymentTypes(storeId: number): void {
    this.storeService.getActivePaymentTypes(storeId).subscribe(paymentTypes => {
      this.paymentTypes = paymentTypes;
    }, err => {
      this.errorService.handler(err.error);
    });
  }

  private async loadMyStores() {
    this.tooManyStores = false;
    const stores = await this.storeService.getAllStoresByCurrentUserAndComponentType();
    if (stores.length > 30) {
      this.tooManyStores = true;
    }
  }
}
